import { createContext, useMemo, type ReactNode } from 'react';

import type { Permissions, PermissionsContextValue, UserType } from './types';
import { getDefaultPermissions } from './defaultPermissions';

/**
 * Context for permissions - null when not inside a provider.
 */
export const PermissionsContext = createContext<PermissionsContextValue | null>(
  null,
);

interface PermissionsProviderProps {
  /**
   * The type of user currently accessing the portal.
   * Determines the default permission set.
   */
  userType: UserType;

  /**
   * Optional explicit permissions to provide.
   * Useful when permissions come from an external source (e.g., Salesforce).
   */
  permissions?: Permissions;

  children: ReactNode;
}

/**
 * Provider component for the permissions system.
 *
 * Manages user type and permissions, making them available to all
 * child components via the usePermissions hook.
 *
 * @example
 * // Basic usage with user type detection
 * <PermissionsProvider userType={isAgent ? 'agent' : 'customer'}>
 *   <App />
 * </PermissionsProvider>
 *
 * @example
 * // With explicit permissions
 * <PermissionsProvider
 *   userType="agent"
 *   permissions={{ 'claim.update': true, 'customer.view': true }}
 * >
 *   <App />
 * </PermissionsProvider>
 */
export const PermissionsProvider = ({
  userType,
  permissions: permissionOverrides,
  children,
}: PermissionsProviderProps) => {
  const value = useMemo((): PermissionsContextValue => {
    const defaultPermissions = getDefaultPermissions(userType);

    // Merge default permissions with any overrides
    const permissions: Permissions = permissionOverrides
      ? { ...defaultPermissions, ...permissionOverrides }
      : defaultPermissions;

    return {
      userType,
      permissions,
      hasPermission: (permission) => permissions[permission] === true,
      isRole: (role) => userType === role,
      isAgent: userType === 'agent',
    };
  }, [userType, permissionOverrides]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
